import React, { useState, useEffect } from 'react';
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Select,
    VStack,
    useToast,
    HStack,
    Input,
} from '@chakra-ui/react';
import api from '../../../auth/apiClient';
import * as XLSX from 'xlsx-js-style';
import { FactoryReceiptItem, FactoryReceiptResponse } from '../../../types/factoryReceipt';
import { trackDownload } from '../../../activity';

interface FactoryReceiptReportPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

interface EstateOption {
    id: number;
    name: string;
    code?: string;
    odoo_id?: number;
}

const DIVISION_COLORS = [
    { header: '92D050', sub: 'C6EFCE', data: 'FFFF00' }, // Green
    { header: '00B0F0', sub: 'B4C6E7', data: 'FFFF00' }, // Cyan
    { header: 'FFC000', sub: 'FFE699', data: 'FFFF00' }, // Yellow / Amber
    { header: 'ED7D31', sub: 'FCE4D6', data: 'FFFF00' }, // Orange
    { header: '70AD47', sub: 'E2EFDA', data: 'FFFF00' }, // Dark Green
    { header: '4472C4', sub: 'D9E1F2', data: 'FFFF00' }, // Blue
];

const FactoryReceiptReportPopup: React.FC<FactoryReceiptReportPopupProps> = ({ isOpen, onClose }) => {
    const todayStr = new Date().toISOString().split('T')[0];

    const [startDate, setStartDate] = useState<string>(todayStr);
    const [endDate, setEndDate] = useState<string>(todayStr);
    const [selectedEstate, setSelectedEstate] = useState<string>('');
    const [estates, setEstates] = useState<EstateOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        const fetchEstates = async () => {
            try {
                const response = await api.get('/inspeksi/web/estate-db');
                if (response.data && Array.isArray(response.data)) {
                    setEstates(response.data);
                    if (response.data.length > 0) {
                        const first = response.data[0];
                        const estateValue = (first.odoo_id ?? first.id).toString();
                        setSelectedEstate(estateValue);
                    }
                }
            } catch (error) {
                console.error("Error fetching estates", error);
            }
        };

        if (isOpen) {
            fetchEstates();
        }
    }, [isOpen]);

    const formatDisplayDate = (dateStr: string) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            const day = date.getDate().toString().padStart(2, '0');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
            const month = months[date.getMonth()];
            return `${day}-${month}`;
        } catch {
            return dateStr;
        }
    };

    const handleExport = async () => {
        if (!selectedEstate || !startDate || !endDate) {
            toast({
                title: 'Error',
                description: 'Silakan pilih Estate, Tanggal Mulai, dan Tanggal Selesai',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            const response = await api.get<FactoryReceiptResponse>('/odoo/factory-receipt-monitoring/factory-receipt-report', {
                params: {
                    estate_id: parseInt(selectedEstate),
                    start_date: startDate,
                    end_date: endDate,
                }
            });

            const data: FactoryReceiptItem[] = response.data?.data || [];

            if (data.length === 0) {
                toast({
                    title: 'Peringatan',
                    description: 'Tidak ada data penerimaan pabrik pada periode dan estate tersebut',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                setIsLoading(false);
                return;
            }

            // Extract distinct divisions sorted
            const divisionSet = new Set<string>();
            data.forEach(item => {
                if (item.division) divisionSet.add(item.division);
            });
            const divisions = Array.from(divisionSet).sort();

            // Setup Excel Workbook
            const workbook = XLSX.utils.book_new();

            // Build Header Rows
            // Row 1: Static headers + Division merged headers
            const row1: any[] = ['TGL', 'NO', 'MASUK', 'Timbang Masuk', 'KELUAR', 'Timbang keluar', 'Owner', 'KONTRAKTOR', 'DRIVER', 'NO POLISI', 'Divisi'];
            divisions.forEach(div => {
                row1.push(div, '', '', '');
            });

            // Row 2: Sub headers (JJG, BRD, BJR, KG)
            const row2: any[] = ['', '', '', '', '', '', '', '', '', '', ''];
            divisions.forEach(() => {
                row2.push('JJG', 'BRD', 'BJR', 'KG');
            });

            const sheetData: any[][] = [row1, row2];

            // Build Data Rows
            data.forEach((item, index) => {
                const row: any[] = [
                    formatDisplayDate(item.wb_date),
                    index + 1,
                    item.time_in ? item.time_in.substring(0, 5) : '',
                    item.weight_in || 0,
                    item.time_out ? item.time_out.substring(0, 5) : '',
                    item.weight_out || 0,
                    item.owner || 'Kebun',
                    '-',
                    item.driver || '',
                    item.nopol || '',
                    item.division || '',
                ];

                // Add values under matching division columns
                divisions.forEach(div => {
                    if (item.division === div) {
                        const bjrVal = item.bjr ? parseFloat(item.bjr.toFixed(2)) : 0;
                        row.push(
                            item.bunch_qty || 0,
                            item.loose_fruit_qty || 0,
                            bjrVal,
                            item.weight || 0
                        );
                    } else {
                        row.push('', '', '', '');
                    }
                });

                sheetData.push(row);
            });

            // Build Total Row
            const totalWeightIn = data.reduce((acc, curr) => acc + (curr.weight_in || 0), 0);
            const totalWeightOut = data.reduce((acc, curr) => acc + (curr.weight_out || 0), 0);
            const totalRow: any[] = ['', '', '', totalWeightIn, '', totalWeightOut, '', '', '', '', 'TOTAL'];
            divisions.forEach(div => {
                const divItems = data.filter(d => d.division === div);
                const totalJjg = divItems.reduce((acc, curr) => acc + (curr.bunch_qty || 0), 0);
                const totalBrd = divItems.reduce((acc, curr) => acc + (curr.loose_fruit_qty || 0), 0);
                const totalKg = divItems.reduce((acc, curr) => acc + (curr.weight || 0), 0);
                const avgBjr = totalJjg > 0 ? parseFloat((totalKg / totalJjg).toFixed(2)) : 0;

                totalRow.push(totalJjg, totalBrd, avgBjr, totalKg);
            });
            sheetData.push(totalRow);

            // Create Worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

            // Merges Configuration
            const merges: XLSX.Range[] = [];
            // Merge static columns vertical (row 0 to 1)
            for (let col = 0; col < 11; col++) {
                merges.push({ s: { r: 0, c: col }, e: { r: 1, c: col } });
            }
            // Merge division headers horizontal (span 4 cols)
            divisions.forEach((_, idx) => {
                const startCol = 11 + idx * 4;
                merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: startCol + 3 } });
            });
            worksheet['!merges'] = merges;

            // Apply Styling
            const totalRows = sheetData.length;
            const totalCols = 11 + divisions.length * 4;

            const thinBorder = {
                top: { style: 'thin', color: { rgb: '000000' } },
                bottom: { style: 'thin', color: { rgb: '000000' } },
                left: { style: 'thin', color: { rgb: '000000' } },
                right: { style: 'thin', color: { rgb: '000000' } },
            };

            for (let r = 0; r < totalRows; r++) {
                for (let c = 0; c < totalCols; c++) {
                    const cellRef = XLSX.utils.encode_cell({ r, c });
                    if (!worksheet[cellRef]) {
                        worksheet[cellRef] = { v: '', t: 's' };
                    }

                    const cell = worksheet[cellRef];
                    cell.s = cell.s || {};
                    cell.s.border = thinBorder;
                    cell.s.alignment = { vertical: 'center', horizontal: 'center' };

                    // Row 0 & Row 1 Header Styling
                    if (r === 0 || r === 1) {
                        cell.s.font = { bold: true, color: { rgb: '000000' } };
                        if (c < 11) {
                            cell.s.fill = { fgColor: { rgb: '1F4E79' } };
                            cell.s.font = { bold: true, color: { rgb: 'FFFFFF' } };
                        } else {
                            const divIdx = Math.floor((c - 11) / 4);
                            const palette = DIVISION_COLORS[divIdx % DIVISION_COLORS.length];
                            const bg = r === 0 ? palette.header : palette.sub;
                            cell.s.fill = { fgColor: { rgb: bg } };
                        }
                    }
                    // Data Rows (r >= 2 && r < totalRows - 1)
                    else if (r < totalRows - 1) {
                        cell.s.font = { size: 10 };
                        if (c >= 11) {
                            // Highlight non-empty division cells in yellow
                            if (cell.v !== '' && cell.v !== null && cell.v !== undefined) {
                                cell.s.fill = { fgColor: { rgb: 'FFFF00' } };
                                if (typeof cell.v === 'number') {
                                    cell.s.alignment = { vertical: 'center', horizontal: 'right' };
                                    const subCol = (c - 11) % 4;
                                    if (subCol === 2) {
                                        cell.z = '#,##0.00;-#,##0.00;"-"';
                                    } else {
                                        cell.z = '#,##0;-#,##0;"-"';
                                    }
                                }
                            }
                        } else {
                            if (c === 0 || c === 1 || c === 2 || c === 4 || c === 10) {
                                cell.s.alignment = { vertical: 'center', horizontal: 'center' };
                            } else if (c === 3 || c === 5) {
                                cell.s.alignment = { vertical: 'center', horizontal: 'right' };
                                if (typeof cell.v === 'number') {
                                    cell.z = '#,##0;-#,##0;"-"';
                                }
                            } else {
                                cell.s.alignment = { vertical: 'center', horizontal: 'left' };
                            }
                        }
                    }
                    // Total Row (r === totalRows - 1)
                    else {
                        cell.s.font = { bold: true, color: { rgb: '000000' } };
                        if (c >= 11) {
                            const divIdx = Math.floor((c - 11) / 4);
                            const palette = DIVISION_COLORS[divIdx % DIVISION_COLORS.length];
                            cell.s.fill = { fgColor: { rgb: palette.header } };
                            cell.s.alignment = { vertical: 'center', horizontal: 'right' };
                            if (typeof cell.v === 'number') {
                                const subCol = (c - 11) % 4;
                                if (subCol === 2) {
                                    cell.z = '#,##0.00;-#,##0.00;"-"';
                                } else {
                                    cell.z = '#,##0;-#,##0;"-"';
                                }
                            }
                        } else {
                            cell.s.fill = { fgColor: { rgb: 'E2EFDA' } };
                            if (c === 3 || c === 5) {
                                cell.s.alignment = { vertical: 'center', horizontal: 'right' };
                                if (typeof cell.v === 'number') {
                                    cell.z = '#,##0;-#,##0;"-"';
                                }
                            }
                        }
                    }
                }
            }

            // Set column widths
            const colWidths: { wch: number }[] = [
                { wch: 10 }, // TGL
                { wch: 6 },  // NO
                { wch: 8 },  // MASUK
                { wch: 14 }, // Timbang Masuk
                { wch: 8 },  // KELUAR
                { wch: 14 }, // Timbang keluar
                { wch: 10 }, // Owner
                { wch: 14 }, // KONTRAKTOR
                { wch: 16 }, // DRIVER
                { wch: 14 }, // NO POLISI
                { wch: 10 }, // Divisi
            ];
            divisions.forEach(() => {
                colWidths.push({ wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 12 });
            });
            worksheet['!cols'] = colWidths;

            // Append Worksheet and Export
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan Penerimaan Pabrik');

            const selectedEstateObj = estates.find(e => (e.odoo_id ?? e.id) === parseInt(selectedEstate) || e.id === parseInt(selectedEstate));
            const estateName = selectedEstateObj?.name || `Estate_${selectedEstate}`;
            const filename = `Laporan_Penerimaan_Pabrik_${estateName}_${startDate}_sd_${endDate}.xlsx`;

            XLSX.writeFile(workbook, filename);
            trackDownload('Laporan Penerimaan Pabrik', { filename });

            toast({
                title: 'Berhasil',
                description: 'Laporan Penerimaan Pabrik berhasil diunduh',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            onClose();
        } catch (error) {
            console.error('Error exporting factory receipt report:', error);
            toast({
                title: 'Error',
                description: 'Gagal mengunduh laporan penerimaan pabrik',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Unduh Laporan Penerimaan Pabrik</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <VStack spacing={4}>
                        <FormControl isRequired>
                            <FormLabel>Estate / Kebun</FormLabel>
                            <Select
                                value={selectedEstate}
                                onChange={(e) => setSelectedEstate(e.target.value)}
                                placeholder="Pilih Estate"
                            >
                                {estates.map((estate) => (
                                    <option key={estate.id} value={estate.odoo_id ?? estate.id}>
                                        {estate.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Tanggal Mulai</FormLabel>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Tanggal Selesai</FormLabel>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </FormControl>

                        <HStack width="100%" justifyContent="flex-end" pt={4}>
                            <Button onClick={onClose} variant="ghost">
                                Batal
                            </Button>
                            <Button
                                colorScheme="green"
                                onClick={handleExport}
                                isLoading={isLoading}
                                loadingText="Mengunduh..."
                            >
                                Download Excel
                            </Button>
                        </HStack>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default FactoryReceiptReportPopup;
